import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  addEscalationLogEntry,
  createEscalation,
  fetchEscalationAttachments,
  fetchEscalationLog,
  fetchEscalations,
  removeEscalationAttachment,
  setEscalationActive,
  transitionEscalation,
  updateEscalation,
  uploadEscalationAttachment,
  type CreateEscalationInput,
  type UpdateEscalationInput,
} from '../api/escalation.api';

export const ESCALATION_KEYS = {
  list: (projectId: string) => ['escalations', projectId] as const,
  log: (projectId: string, escalationId: string) => ['escalations', projectId, escalationId, 'log'] as const,
  attachments: (projectId: string, escalationId: string) => ['escalations', projectId, escalationId, 'attachments'] as const,
};

export function useEscalations(projectId: string) {
  return useQuery({
    queryKey: ESCALATION_KEYS.list(projectId),
    queryFn: () => fetchEscalations(projectId),
    enabled: Boolean(projectId),
  });
}

export function useCreateEscalation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEscalationInput) => createEscalation(projectId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.list(projectId) }),
  });
}

export function useUpdateEscalation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ escalationId, data }: { escalationId: string; data: UpdateEscalationInput }) =>
      updateEscalation(projectId, escalationId, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.list(projectId) }),
  });
}

export function useTransitionEscalation(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ escalationId, toStep }: { escalationId: string; toStep: string }) =>
      transitionEscalation(projectId, escalationId, toStep),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.list(projectId) }),
  });
}

// Unbound counterpart to useTransitionEscalation(projectId) above — projectId travels in the
// mutate payload instead of being fixed at hook-creation, so Home's cross-project Escalations
// tab can transition rows from different projects through one hook instance.
export function useTransitionEscalationAcrossProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, escalationId, toStep }: { projectId: string; escalationId: string; toStep: string }) =>
      transitionEscalation(projectId, escalationId, toStep),
    onSuccess: (_result, { projectId }) => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.list(projectId) }),
  });
}

export function useSetEscalationActive(projectId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ escalationId, active }: { escalationId: string; active: boolean }) =>
      setEscalationActive(projectId, escalationId, active),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.list(projectId) }),
  });
}

export function useEscalationLog(projectId: string, escalationId: string) {
  return useQuery({
    queryKey: ESCALATION_KEYS.log(projectId, escalationId),
    queryFn: () => fetchEscalationLog(projectId, escalationId),
    enabled: Boolean(escalationId),
  });
}

export function useAddEscalationLogEntry(projectId: string, escalationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (note: string) => addEscalationLogEntry(projectId, escalationId, note),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.log(projectId, escalationId) }),
  });
}

export function useEscalationAttachments(projectId: string, escalationId: string) {
  return useQuery({
    queryKey: ESCALATION_KEYS.attachments(projectId, escalationId),
    queryFn: () => fetchEscalationAttachments(projectId, escalationId),
    enabled: Boolean(escalationId),
  });
}

export function useUploadEscalationAttachment(projectId: string, escalationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadEscalationAttachment(projectId, escalationId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.attachments(projectId, escalationId) }),
  });
}

export function useRemoveEscalationAttachment(projectId: string, escalationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => removeEscalationAttachment(projectId, escalationId, attachmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ESCALATION_KEYS.attachments(projectId, escalationId) }),
  });
}
